import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { InputSwitch } from 'primereact/inputswitch';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@/config/i18n';
import { apiRequest } from '@/services';
import { UserFormType, userFormSchema } from '@/shared/schema/user';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { TextareaCustomForm } from '@/components/common/ui/textarea';
import UploadImage from '@/components/common/ui/upload/uploadImage';
import { useSelectorUserInfo } from '@/components/auth/AuthContext';
import {
  getValueFromSelect,
  normalizeTimeZone,
  toastErrorMessage,
  toastMessage,
} from '@/lib/utils';
import usePathName from '@/hooks/usePathName';
import {
  ASSIGN_COACH_ERROR_CODE,
  PAYMENT_STATUS,
  ROLE,
  STAGE_GRADUATION_STATUS,
  SUBSCRIPTION_PAYMENT_STATUS,
} from '@/shared/constants/user';
import PATH from '@/routes/path';
import {
  HTTP_STATUS,
  MAX_LENGTH,
  UPDATE_STATUS_USER_ERROR_CODE,
} from '@/shared/constants';
import { useLoading } from '@/components/common/loader/LoadingContext';
import { ToastContext } from '@/components/common/CommonToast';
import useScrollToError from '@/hooks/useScrollToError';
import InfinitySelect from '../common/ui/select';
import { HistoryChangePlanType } from '@/types/plan';
import { OptionType } from '@/types/api';
import { UserInfoType } from '@/types/auth';
import { GraduationTermPayload, GraduationType } from '@/types/graduation';
import { IconArrow, IconCheckboxChecked, IconCheckboxEmpty } from '../icons';
import DialogCustom from '../common/ui/dialog';
import PopupHistoryChangePlan from '@/pages/profile/components/PopupHistoryChangePlan';

type Props = {
  id?: number;
};

let controllerMentor = new AbortController();
let controllerSupervisingMentor = new AbortController();

const UserForm: React.FC<Props> = ({ id }) => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const [isActive, setIsActive] = useState(true);
  const [originalActive, setOriginalActive] = useState(false);
  const [defaultImage, setDefaultImage] = useState();
  const [graduations, setGraduations] = useState<GraduationType[]>([]);
  const [dialogConfirmPaidVisible, setDialogConfirmPaidVisible] =
    useState(false);
  const [dataUserDetail, setDataUserDetail] = useState<UserInfoType>();
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [pendingSwitchValue, setPendingSwitchValue] = useState<boolean | null>(
    null,
  );
  const [visibleModalHistoryChangePlan, setVisibleModalHistoryChangePlan] =
    useState(false);
  const [historyChangePlan, setHistoryChangePlan] = useState<
    HistoryChangePlanType[]
  >([]);
  const [supervisingMentorOptions, setSupervisingMentorOptions] = useState<
    OptionType[]
  >([]);

  const {
    current_roles: userDetailCurrentRole,
    plan_type,
    stage_graduation_status,
    subscription_payment_status,
    plan: currentPlan,
  } = dataUserDetail || {};

  const pathName = usePathName();
  const navigate = useNavigate();
  const { scrollToError } = useScrollToError();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const { currentUser } = useSelectorUserInfo();
  const { current_roles } = currentUser;
  const userRole = current_roles?.[0] || '';

  const role = pathName.includes(PATH.student_management)
    ? ROLE.STUDENT
    : ROLE.TEACHER;
  const isCoachCharge =
    userDetailCurrentRole?.[0] === ROLE.TEACHER &&
    plan_type !== PAYMENT_STATUS.FREE;
  const isPaid =
    stage_graduation_status === STAGE_GRADUATION_STATUS.PAID &&
    subscription_payment_status === SUBSCRIPTION_PAYMENT_STATUS.SUSPENDED;

  const formMethods = useForm<UserFormType>({
    mode: 'onChange',
    resolver: yupResolver(userFormSchema),
    shouldFocusError: false,
    defaultValues: {
      require_password_update: true,
      is_student: role === ROLE.STUDENT ? true : false,
      status: true,
      email: '',
      password: '',
      password_confirmation: '',
      first_name_kanji: '',
      last_name_kanji: '',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      phone_number: '',
      post_code: '',
      address: '',
      plan: null,
      coach_mentor_id: null,
      ...(role === ROLE.STUDENT ? {} : { student_limit: 10, graduation_term_id: undefined }),
    },
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = formMethods;

  const currentUserTermId =
    dataUserDetail?.graduation_terms && typeof dataUserDetail.graduation_terms === 'object'
      ? (dataUserDetail.graduation_terms as GraduationTermPayload).id
      : undefined;

  const graduationTermOptions = useMemo(() => {
    return graduations
      .filter((g) => g.is_visible !== false)
      .flatMap((g) => {
        const terms = (g.terms ?? []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        return terms
          .filter(
            (term) =>
              term.id != null &&
              (term.status !== 'CLOSED' || term.id === currentUserTermId),
          )
          .map((term) => ({
            label: `${g.period_number}期ー${term.name ?? ''}`,
            value: term.id as number,
          }));
      });
  }, [graduations, currentUserTermId]);

  const getAllFieldNames = () => {
    const values = getValues();
    return Object.keys(values);
  };

  const getGraduationList = useCallback(async () => {
    const { data } = await apiService.guest.collection('graduations', {
      method: 'GET',
      params: {
        timezone: normalizeTimeZone(
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        ),
      },
    });

    setGraduations(data?.data || []);
  }, []);

  const getPlanChangeHistoryList = useCallback(async () => {
    const { data } = await apiService.users.collection(
      `${id}/history-change-plans`,
      {
        method: 'GET',
      },
    );

    setHistoryChangePlan(data?.data || []);
  }, []);

  async function getUserDetail(id: number) {
    setLoadingGlobal(true);
    const { data: user } = await apiService.users.show(id);

    if (user?.data) {
      setDefaultValue(user.data);
      setDefaultImage(user.data.profile_image);
      setDataUserDetail(user.data);
    }
    setLoadingGlobal(false);
  }

  function setDefaultValue(data: any) {
    const specialField = [
      'mentor_id',
      'coach_mentor_id',
      'gender',
      'date_of_birth',
      'status',
      'require_password_update',
      'graduation_term_id',
    ];
    getAllFieldNames().forEach((field: any) => {
      const val = data[field];

      if (val && !specialField.includes(field)) {
        setValue(field, val);
      }

      if (field === 'mentor_id') {
        setValue(field, {
          label: data.mentor?.full_name_kanji,
          value: String(data.mentor?.id),
          subLabel: data.mentor?.email,
        });
      }

      // `supervising_mentor` = the mentor supervising this coach. Distinct from
      // `mentor` above, which is the coach supervising a student.
      if (field === 'coach_mentor_id') {
        const supervisingMentor = data.supervising_mentor;
        if (!supervisingMentor) {
          setValue(field, null);
          setSupervisingMentorOptions([]);
          return;
        }
        const option = {
          label: supervisingMentor.full_name_kanji,
          value: String(supervisingMentor.id),
          subLabel: supervisingMentor.email,
        };
        setValue(field, option as any);
        // A locked mentor is missing from the dropdown but must still display.
        setSupervisingMentorOptions([option]);
      }

      if (field === 'gender') {
        setValue(field, { label: t(`common.gender.${val}`), value: val });
      }

      if (field === 'status') {
        const status = data.status === 'active' ? true : false;
        setValue(field, status);
        setIsActive(status);
        setOriginalActive(status);
      }

      if (field === 'date_of_birth') {
        if (val) {
          setValue(field, new Date(val));
        }
      }

      if (field === 'require_password_update') {
        setValue(field, false);
      }

      if (field === 'plan') {
        if (val) {
          setValue(field, val.id);
        }
      }
    });

    const graduationTerms = data.graduation_terms;
    const termId =
      graduationTerms && typeof graduationTerms === 'object'
        ? (graduationTerms as GraduationTermPayload).id
        : undefined;
    if (termId != null) {
      setValue('graduation_term_id', Number(termId));
    }
  }

  useEffect(() => {
    if (!id) {
      setValue('gender', {
        label: t(`common.gender.male`),
        value: 'male',
      } as unknown as any);

      if (!isEmpty(currentUser) && userRole === ROLE.TEACHER) {
        setValue('mentor_id', {
          label: currentUser.full_name_kanji,
          value: String(currentUser.id),
          subLabel: currentUser.email,
        } as any);
        return;
      }
      return;
    }

    getUserDetail(id);
  }, [id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToError();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [errors]);

  async function onSubmit() {
    setLoadingGlobal(true);
    const payload = getPayload();
    let data = null;
    let error = null;
    if (id) {
      const { data: user, error: userError } = await apiService.users.update(
        id,
        payload,
      );
      data = user;
      error = userError;
    } else {
      const { data: user, error: userError } =
        await apiService.users.create(payload);
      data = user;
      error = userError;
    }
    if (error) {
      setConfirmPaid(false);
      handleError(error);
      setLoadingGlobal(false);
      return;
    }

    if (data) {
      let msg =
        role === ROLE.STUDENT
          ? t('user.student_success')
          : t('user.coach_success');
      if (id) {
        msg =
          role === ROLE.STUDENT
            ? t('user.update_student_success')
            : t('user.update_coach_success');
      }
      toast?.current?.show(toastMessage(msg));
      setLoadingGlobal(false);
      setConfirmPaid(false);
      if (role === ROLE.TEACHER) {
        navigate(PATH.coach_management);
        return;
      }
      navigate(PATH.student_management);
    }
  }

  function getPayload() {
    const status = isActive ? 'active' : 'inactive';
    const {
      date_of_birth,
      password,
      password_confirmation,
      student_limit,
      coach_mentor_id,
      ...rest
    } = getValues();
    const valuesSelect = getValueFromSelect(rest, [
      'mentor_id',
      'gender',
    ]);
    // Handled outside getValueFromSelect on purpose: that helper drops falsy
    // values, so clearing the field would send nothing and silently keep the
    // existing link instead of unassigning the mentor.
    const selectedMentorId =
      coach_mentor_id && (coach_mentor_id as any).value
        ? Number((coach_mentor_id as any).value)
        : null;
    // On create only send the field when a mentor was actually picked; on edit
    // always send it, so clearing the select unassigns the current mentor.
    const supervisingMentorPayload =
      role !== ROLE.TEACHER || (!id && !selectedMentorId)
        ? {}
        : { coach_mentor_id: selectedMentorId };
    const dateOfBirth = date_of_birth
      ? dayjs(date_of_birth.toString()).format('YYYY-MM-DD')
      : '';
    const passwordCustom = !password ? undefined : password;
    const passwordConfirmationCustom = !password_confirmation
      ? undefined
      : password_confirmation;

    const statusParams = id ? (isActive === originalActive ? undefined : status) : status;

    const shouldChargeImmediately =
      isPaid && isActive ? confirmPaid : undefined;

    const graduationTermId =
      rest.graduation_term_id != null && typeof rest.graduation_term_id === 'object' && 'value' in rest.graduation_term_id
        ? Number((rest.graduation_term_id as { value: number }).value)
        : rest.graduation_term_id != null
          ? Number(rest.graduation_term_id)
          : undefined;

    return {
      ...rest,
      ...valuesSelect,
      ...supervisingMentorPayload,
      ...(graduationTermId != null ? { graduation_term_id: graduationTermId } : {}),
      role,
      status: statusParams,
      date_of_birth: dateOfBirth,
      password: passwordCustom,
      password_confirmation: passwordConfirmationCustom,
      should_charge_immediately: shouldChargeImmediately,
      ...(role === ROLE.TEACHER
        ? { student_limit: Number(student_limit) }
        : {}),
    };
  }

  function handleError(error: any) {
    const { status_code, errors, error_code } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      errors.forEach((e: any) => {
        const { field, message, error_code } = e;
        if (error_code === ASSIGN_COACH_ERROR_CODE.MENTOR_INVALID) {
          setError('coach_mentor_id', {
            message: message || t('user.assign_coach_error.mentor_invalid'),
          });
          return;
        }
        setError(field, { message });
      });

      const errorLimitStudent = errors?.find(
        (e: any) => e.error_code === 'MFS-E-LEARNING-ADMIN-USER-169',
      );
      console.log(errorLimitStudent);
      if (errorLimitStudent) {
        toast?.current?.show(toastErrorMessage(errorLimitStudent?.message));
      }

      return;
    }

    if (
      error_code === UPDATE_STATUS_USER_ERROR_CODE.RESUME_SUBSCRIPTION_ERROR
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.resume_subscription_error'),
        ),
      );
    }

    if (error_code === UPDATE_STATUS_USER_ERROR_CODE.ACTIVE_CHANGE_FAILED) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.active_change_failed'),
        ),
      );
    }

    if (error_code === UPDATE_STATUS_USER_ERROR_CODE.TIMEOUT) {
      toast?.current?.show(
        toastErrorMessage(t('user_list.change_status_error.timeout')),
      );
    }

    if (
      error_code ===
      UPDATE_STATUS_USER_ERROR_CODE.INACTIVE_HAVE_PLAN_OR_GRADUATION_INVALID
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t(
            'user_list.change_status_error.inactive_have_plan_or_graduation_invalid',
          ),
        ),
      );
    }

    if (error_code === UPDATE_STATUS_USER_ERROR_CODE.NOT_FOUND_SUBSCRIPTION) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.not_found_subscription'),
        ),
      );
    }

    if (
      error_code === UPDATE_STATUS_USER_ERROR_CODE.NOT_FOUND_SUBSCRIPTION_TOKEN
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.not_found_subscription'),
        ),
      );
    }

    if (
      error_code ===
      UPDATE_STATUS_USER_ERROR_CODE.INACTIVE_HAVE_PLAN_OR_GRADUATION_INVALID_TOKEN
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t(
            'user_list.change_status_error.inactive_have_plan_or_graduation_invalid',
          ),
        ),
      );
    }

    if (
      error_code ===
      UPDATE_STATUS_USER_ERROR_CODE.RESUME_SUBSCRIPTION_ERROR_TOKEN
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.active_change_failed'),
        ),
      );
    }

    const msg =
      role === ROLE.STUDENT ? t('user.student_fail') : t('user.coach_fail');
    toast?.current?.show(toastErrorMessage(msg));
  }

  // Coaches available as the supervisor of a student (student -> coach).
  const fetchMentorOptions = useCallback(async (params: any) => {
    if (controllerMentor.abort) controllerMentor.abort();
    controllerMentor = new AbortController();
    const { signal } = controllerMentor;
    return await apiService.users.index(
      {
        ...params,
        status: 'active',
        role: ROLE.TEACHER,
      },
      { signal },
    );
  }, []);

  // Mentors available as the supervisor of a coach (coach -> mentor). Only
  // active mentors are listed; the one currently assigned is merged in through
  // `defaultOption` so a locked mentor still shows up.
  const fetchSupervisingMentorOptions = useCallback(async (params: any) => {
    if (controllerSupervisingMentor.abort) controllerSupervisingMentor.abort();
    controllerSupervisingMentor = new AbortController();
    const { signal } = controllerSupervisingMentor;
    return await apiService.users.index(
      {
        ...params,
        status: 'active',
        role: ROLE.MENTOR,
      },
      { signal },
    );
  }, []);

  const onBeforeUploadAvatar = (event: any) => {
    const MAX_SIZE_MB = 20;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    const file = event.files[0];
    if (file.size > MAX_SIZE_BYTES) {
      event.options.clear(); // clear the files from the upload
      toast?.current?.show(toastErrorMessage(t('common.file.image_max_20MB')));
      return;
    }
  };

  function handleNavigateBack() {
    let pathBack = PATH.student_management;
    if (role === ROLE.TEACHER) {
      pathBack = PATH.coach_management;
    }

    navigate(pathBack);
  }

  function handleSwitchChange(value: boolean) {
    if (isPaid && value) {
      setDialogConfirmPaidVisible(true);
      setPendingSwitchValue(value);
    } else if (
      subscription_payment_status === SUBSCRIPTION_PAYMENT_STATUS.CANCELED &&
      value
    ) {
      toast?.current?.show(
        toastErrorMessage(t('user_list.toast.cannot_active_toggle')),
      );
      return;
    } else {
      setIsActive(value);
    }
  }

  const handleConfirmPaid = () => {
    if (pendingSwitchValue !== null) {
      setIsActive(pendingSwitchValue);
      setPendingSwitchValue(null);
    }
    setDialogConfirmPaidVisible(false);
  };

  useEffect(() => {
    getGraduationList();
  }, [getGraduationList]);

  useEffect(() => {
    if (!id || !currentUserTermId || !graduationTermOptions.length) return;
    const hasOption = graduationTermOptions.some((opt) => Number(opt.value) === Number(currentUserTermId));
    if (hasOption) {
      setValue('graduation_term_id', Number(currentUserTermId));
    }
  }, [id, currentUserTermId, graduationTermOptions, setValue]);

  useEffect(() => {
    if (isCoachCharge) {
      getPlanChangeHistoryList();
    }
  }, [isCoachCharge, getPlanChangeHistoryList]);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          className="mt-[16px]"
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          <div className="form-item flex">
            <div className="label w-56 flex">
              <span>{t('user.status')}</span>
            </div>
            <InputSwitch
              name="status"
              checked={isActive}
              onChange={(e) => handleSwitchChange(e.value)}
            />
            <span className="ml-2">
              {isActive ? t('user.active') : t('user.in_active')}
            </span>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.avatar')}</span>
            </div>
            <div className="w-[512px] flex">
              <UploadImage
                idChooseBtn="choose-image"
                className="relative w-[366px] h-[126px] flex-full-center border border-gray-200"
                name="profile_image_id"
                accept=".svg,.jpg,.jpeg,.png"
                defaultUrl={defaultImage}
                onBeforeUpload={onBeforeUploadAvatar}
                maxSize={5}
                handleUploaded={(id) => {
                  setValue('profile_image_id', id);
                }}
                textUpload="ファイルを選択"
                textSubUpload="またはここにファイルをドロップ"
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.email')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="email"
              wrapClass="w-[512px]"
              disabled={!!id}
            />
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.phone_number')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm
                name="phone_number"
                wrapClass="w-full"
                maxLength={MAX_LENGTH.PHONE_NUMBER}
                isNumber
              />
              <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
                {t('user.phone_hint')}
              </p>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.password')}</span>
              {!id && <span className="text-error-500">*</span>}
            </div>
            <div className="w-[512px]">
              <div className="grid grid-cols-2 gap-6">
                <InputTextCustomForm
                  name="password"
                  type="text"
                  isHideValue={true}
                  placeholder={t('user.password')}
                  maxLength={MAX_LENGTH.PASSWORD}
                />
                <InputTextCustomForm
                  name="password_confirmation"
                  type="text"
                  isHideValue={true}
                  placeholder={t('user.password_confirm')}
                  maxLength={MAX_LENGTH.PASSWORD}
                />
              </div>
              <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
                {t('user.password_hint')}
              </p>
            </div>
          </div>

          {role === ROLE.STUDENT && (
            <div className="form-item flex mt-[20px]">
              <div className="label w-56 flex">
                <span>{t('user.mentor')}</span>
                <span className="text-error-500">*</span>
              </div>
              <InfinitySelect
                placeholder=""
                wrapClass="w-[512px]"
                name="mentor_id"
                service={
                  [ROLE.ADMIN, ROLE.SUPPER_ADMIN].includes(userRole) &&
                  fetchMentorOptions
                }
                fieldLabels={{
                  label: 'full_name_kanji',
                  value: 'id',
                  subLabel: 'email',
                }}
                filterOption={() => true}
                isDisabled={userRole === ROLE.TEACHER}
              />
            </div>
          )}

          <div className="break-line border-t border-gray-200 h-[1px] mt-[20px]"></div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.kanji_name')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px] grid grid-cols-2 gap-6">
              <InputTextCustomForm
                name="last_name_kanji"
                placeholder={t('user.last_name_kanji_placeholder')}
              />
              <InputTextCustomForm
                name="first_name_kanji"
                placeholder={t('user.first_name_kanji_placeholder')}
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.name')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px] grid grid-cols-2 gap-6">
              <InputTextCustomForm
                name="last_name"
                placeholder={t('user.last_name_placeholder')}
              />
              <InputTextCustomForm
                name="first_name"
                placeholder={t('user.first_name_placeholder')}
              />
            </div>
          </div>

          {/* コーチ担当者: the mentor supervising THIS coach (1 coach : 1 mentor).
              Do not confuse with `mentor_id` above, which is the coach
              supervising a student — opposite direction, different subject. */}
          {role === ROLE.TEACHER && (
            <div className="form-item flex mt-[20px]">
              <div className="label w-56 flex">
                <span>{t('user.supervising_mentor')}</span>
              </div>
              <InfinitySelect
                placeholder=""
                wrapClass="w-[512px]"
                name="coach_mentor_id"
                service={
                  [ROLE.ADMIN, ROLE.SUPPER_ADMIN].includes(userRole) &&
                  fetchSupervisingMentorOptions
                }
                defaultOption={supervisingMentorOptions}
                fieldLabels={{
                  label: 'full_name_kanji',
                  value: 'id',
                  subLabel: 'email',
                }}
                filterOption={() => true}
              />
            </div>
          )}

          {role === ROLE.TEACHER && (
            <div className="form-item flex mt-[20px]">
              <div className="label w-56 flex">
                <span>{t('user.graduation_terms')}</span>
                <span className="text-error-500">*</span>
              </div>
              <InfinitySelect
                placeholder=""
                wrapClass="w-[512px]"
                name="graduation_term_id"
                defaultOption={graduationTermOptions}
                focusDefaultOption={false}
                getOptionValue={(o: { value?: number } | number) => (o && typeof o === 'object' && 'value' in o ? (o as { value: number }).value : o)}
                getOptionLabel={(o: { label?: string } | number) => (o && typeof o === 'object' && 'label' in o ? (o as { label: string }).label : String(o ?? ''))}
                onMenuOpen={() => {
                  setTimeout(() => {
                    const selectedEl = document.getElementsByClassName(
                      'select__option--is-selected',
                    )[0];
                    if (selectedEl) {
                      selectedEl.scrollIntoView({});
                    }
                  }, 15);
                }}
              />
            </div>
          )}

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.date_of_birth')}</span>
            </div>
            <div className="w-[512px]">
              <CalendarCustomForm
                wrapClass="w-full"
                name="date_of_birth"
                dateFormat="yy/mm/dd"
                maxDate={new Date()}
                showIcon
                icon={() => <i className="pi pi-calendar text-[18px]" />}
                placeholder="yyyy/mm/dd"
                panelClassName="calendar-date-of-birth"
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.gender')}</span>
            </div>
            <div className="w-[512px]">
              <InfinitySelect
                name="gender"
                placeholder=""
                wrapClass="w-[512px]"
                isClearable={false}
                defaultOption={[
                  { value: 'male', label: t('common.gender.male') },
                  { value: 'female', label: t('common.gender.female') },
                  { value: 'other', label: t('common.gender.other') },
                ]}
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.post_code')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm
                wrapClass="w-full"
                name="post_code"
                placeholder="XXXXXXX"
                maxLength={MAX_LENGTH.POSTCODE}
              />
              <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
                {t('user.postcode_hint')}
              </p>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user.address')}</span>
              <span className="text-error-500">*</span>
            </div>
            <TextareaCustomForm
              name="address"
              wrapClass="w-[512px]"
              maxLength={MAX_LENGTH.ADDRESS}
            />
          </div>

          {role === ROLE.TEACHER && !isCoachCharge && (
            <div className="form-item flex mt-[20px]">
              <div className="label w-56 flex">
                <span>{t('user.student_limit')}</span>
                <span className="text-error-500">*</span>
              </div>
              <div className="w-[512px]">
                <InputTextCustomForm
                  wrapClass="w-full"
                  name="student_limit"
                  isNumber
                />
              </div>
            </div>
          )}

          {id && isCoachCharge && (
            <>
              <div className="form-item flex mt-[20px]">
                <div className="label w-56 flex">
                  <span>{t('profile.plan')}</span>
                </div>
                <div className="w-[512px] p-4 bg-gray-100 rounded-lg">
                  <div className="px-[10px] py-[14px] border border-gray-300 bg-gray-100 rounded-lg opacity-70 overflow-hidden relative cursor-not-allowed flex items-center">
                    {currentPlan ? (
                      <div className="w-[440px]">
                        <span className="font-bold">{`${currentPlan.name} - ${currentPlan.price.toLocaleString()}円/`}</span>
                        {`月（税込）・受講生管理可能人数 ${currentPlan.student_limit}人`}
                      </div>
                    ) : (
                      <span className="text-gray-500">
                        {t('common.form.empty')}
                      </span>
                    )}
                    <IconArrow />
                  </div>
                  <div
                    className="text-primary-500 text-md font-normal flex items-center cursor-pointer mt-4 justify-end"
                    onClick={() => setVisibleModalHistoryChangePlan(true)}
                  >
                    プラン変更履歴
                    <IconArrow />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px]">
            <ButtonCustom
              className="w-[140px]"
              type="button"
              severity="secondary"
              onClick={() => handleNavigateBack()}
            >
              {t('common.form.cancel')}
            </ButtonCustom>
            <ButtonCustom
              className="ml-2 w-[140px]"
              type="submit"
              loading={loadingGlobal}
            >
              {id ? t('common.form.save') : t('common.form.new')}
            </ButtonCustom>
          </div>
        </form>
      </FormProvider>

      <DialogCustom
        header={t('user_list.popup.title')}
        visible={dialogConfirmPaidVisible}
        onHide={() => {
          setDialogConfirmPaidVisible(false);
          setConfirmPaid(false);
        }}
        className="w-[400px]"
        footer={
          <div className="field">
            <div className="flex justify-end">
              <ButtonCustom
                type="button"
                className="cancel-button p-button-sm mr-2"
                severity="secondary"
                onClick={() => {
                  setDialogConfirmPaidVisible(false);
                  setConfirmPaid(false);
                }}
              >
                {t('user_list.popup.btn_cancel')}
              </ButtonCustom>
              <ButtonCustom type="button" onClick={handleConfirmPaid}>
                {t('user_list.popup.btn_ok')}
              </ButtonCustom>
            </div>
          </div>
        }
      >
        <div>{t('user_list.popup.content')}</div>
        <div className="flex items-center mt-3 whitespace-pre-wrap">
          <div
            className="mr-[10px] cursor-pointer"
            onClick={() => setConfirmPaid(!confirmPaid)}
          >
            {confirmPaid ? <IconCheckboxChecked /> : <IconCheckboxEmpty />}
          </div>
          {t('user_list.popup.checkbox_content')}
        </div>
      </DialogCustom>

      <PopupHistoryChangePlan
        data={historyChangePlan}
        isOpen={visibleModalHistoryChangePlan}
        onClose={() => setVisibleModalHistoryChangePlan(false)}
      />
    </>
  );
};

export default UserForm;
